import i18next from "i18next"
import { formatCountWithPerc, NormalizedRiskMetrics } from "../utils/riskControlMetrics"

const METRIC_HEADER_CLASS =
  "text-[9px] font-semibold uppercase tracking-wide text-gray-400 text-right leading-none whitespace-nowrap pl-0.5 pr-0.5 pb-1";

const METRIC_HEADER_LAST_CLASS = `${METRIC_HEADER_CLASS} pr-2.5`;

const METRIC_VALUE_TD_CLASS = "text-right align-middle pl-0.5 pr-0.5 py-0.5 whitespace-nowrap";

const METRIC_VALUE_LAST_TD_CLASS = `${METRIC_VALUE_TD_CLASS} pr-2.5`;

const METRIC_VALUE_CLASS =
  "text-[10px] font-semibold tabular-nums leading-tight inline-block";

type MetricLabelKey =
  | "indicators_avg_progress"
  | "indicators_completed"
  | "indicators_overdue";

type MetricTone = "neutral" | "success" | "danger";

const toneClasses: Record<MetricTone, string> = {
  neutral: "text-[#4B4B4B]",
  success: "text-emerald-700",
  danger: "text-red-600",
};

const metricTones = (metrics: NormalizedRiskMetrics) => ({
  avg: "neutral" as MetricTone,
  completed: metrics.completedCount > 0 ? ("success" as MetricTone) : ("neutral" as MetricTone),
  overdue: metrics.overdueCount > 0 ? ("danger" as MetricTone) : ("neutral" as MetricTone),
});

const MetricValue: React.FC<{ value: string; tone?: MetricTone }> = ({
  value,
  tone = "neutral",
}) => (
  <span className={`${METRIC_VALUE_CLASS} ${toneClasses[tone]}`}>{value}</span>
);

const MetricValueCells: React.FC<{
  metrics: NormalizedRiskMetrics;
  summary?: boolean;
  accentColor?: string;
}> = ({ metrics, summary = false, accentColor = "#005030" }) => {
  const tones = metricTones(metrics);
  const summaryBg = summary ? { backgroundColor: `${accentColor}12` } : undefined;
  const summaryTd = summary
    ? `${METRIC_VALUE_TD_CLASS} py-1.5`
    : METRIC_VALUE_TD_CLASS;
  const summaryTdLast = summary
    ? `${METRIC_VALUE_LAST_TD_CLASS} py-1.5`
    : METRIC_VALUE_LAST_TD_CLASS;

  return (
    <>
      <td className={summaryTd} style={summaryBg}>
        <MetricValue value={`${metrics.avgProgressPerc}%`} tone={tones.avg} />
      </td>
      <td className={summaryTd} style={summaryBg}>
        <MetricValue
          value={formatCountWithPerc(
            metrics.completedCount,
            metrics.totalControls,
            metrics.completedPerc
          )}
          tone={tones.completed}
        />
      </td>
      <td
        className={`${summaryTdLast}${summary ? " rounded-r-md" : ""}`}
        style={summaryBg}
      >
        <MetricValue
          value={formatCountWithPerc(
            metrics.overdueCount,
            metrics.totalControls,
            metrics.overduePerc
          )}
          tone={tones.overdue}
        />
      </td>
    </>
  );
};

type IndicatorMetricsBlockProps = {
  metrics: NormalizedRiskMetrics;
  accentColor?: string;
  children: React.ReactNode;
};

export const IndicatorMetricsBlock: React.FC<IndicatorMetricsBlockProps> = ({
  metrics,
  accentColor = "#005030",
  children,
}) => (
  <table className="w-full border-separate border-spacing-0 table-fixed text-[11px]">
    <colgroup>
      <col style={{ width: "52%" }} />
      <col style={{ width: "16%" }} />
      <col style={{ width: "16%" }} />
      <col style={{ width: "16%" }} />
    </colgroup>
    <thead>
      <tr>
        <th scope="col" className="p-0" aria-hidden />
        {(["indicators_avg_progress", "indicators_completed", "indicators_overdue"] as MetricLabelKey[]).map(
          (key) => (
            <th
              key={key}
              scope="col"
              className={
                key === "indicators_overdue" ? METRIC_HEADER_LAST_CLASS : METRIC_HEADER_CLASS
              }
            >
              {i18next.t(key)}
            </th>
          )
        )}
      </tr>
    </thead>
    <tbody>
      <tr>
        <td
          className="text-left text-[10px] font-semibold text-gray-500 rounded-l-md py-1.5 pl-1.5 pr-1 truncate align-middle"
          style={{ backgroundColor: `${accentColor}12` }}
        >
          {i18next.t("indicators_subcategory_summary")}
        </td>
        <MetricValueCells metrics={metrics} summary accentColor={accentColor} />
      </tr>
      {children}
    </tbody>
  </table>
);

export const IndicatorRiskMetricsRow: React.FC<{
  metrics: NormalizedRiskMetrics;
  name: React.ReactNode;
}> = ({ metrics, name }) => (
  <tr>
    <td className="p-0 align-middle">
      {name}
    </td>
    <MetricValueCells metrics={metrics} />
  </tr>
);
